// WhatsAppFloatingButton.jsx
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const WhatsAppFloatingButton = () => {
  const businessNumber = "+919484794843"; // keep as you like
  const waNumber = businessNumber.replace(/\D/g, ""); // remove + and non-digits for wa.me
  const whatsappLink = `https://wa.me/${waNumber}`;

  // Check sessionStorage on mount - persist closed state until refresh
  const [boxVisible, setBoxVisible] = useState(() => {
    const stored = sessionStorage.getItem('callBoxClosed');
    return stored !== 'true';
  });

  const handleClose = () => {
    setBoxVisible(false);
    sessionStorage.setItem('callBoxClosed', 'true');
  };

  return (
    <>
      <style>{styles}</style>

      {/* Floating message box (left of whatsapp button) */}
      <div
        className={`wfb-message ${boxVisible ? "wfb-show" : "wfb-hide"}`}
        role="region"
        aria-label="Call us"
      >
        <div className="wfb-message__left">
          <div className="wfb-phone-icon" aria-hidden="true">
            <img src="/customer-service.gif" alt="Call" className="wfb-gif" />
          </div>
          <div className="wfb-message__text">
            <div className="wfb-label">Call us at</div>
            <a
              href={`tel:${waNumber}`}
              className="wfb-number"
              onClick={(e) => {
                /* normal tel behaviour */
              }}
            >
              {businessNumber}
            </a>
          </div>
        </div>

        <button
          className="wfb-close"
          onClick={handleClose}
          aria-label="Close call box"
          title="Close"
        >
          <X size={16} />
        </button>
      </div>

      {/* WhatsApp floating round button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="wfb-whatsapp"
        aria-label="Chat on WhatsApp"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="white"
          aria-hidden="true"
        >
          <path d="M20.52 3.48A11.78 11.78 0 0 0 12 0C5.37 0 0 5.37 0 12a11.9 11.9 0 0 0 1.72 6.1L0 24l6.26-1.64A12 12 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.22-3.48-8.52zM12 21.82a9.7 9.7 0 0 1-5.07-1.4l-.36-.21-3.72.97 1-3.63-.24-.37A9.74 9.74 0 0 1 2.18 12C2.18 6.6 6.6 2.18 12 2.18c2.6 0 5.04 1 6.88 2.82A9.63 9.63 0 0 1 21.82 12c0 5.4-4.42 9.82-9.82 9.82zm5.42-7.45c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.28-.47-2.44-1.5-.9-.8-1.5-1.78-1.68-2.08-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.25-.6-.5-.52-.67-.52-.17 0-.37-.02-.57-.02-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.48 0 1.45 1.07 2.86 1.22 3.07.15.2 2.12 3.23 5.14 4.53.72.31 1.28.5 1.72.64.72.23 1.38.2 1.9.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
        </svg>
      </a>
    </>
  );
};

export default WhatsAppFloatingButton;

/* -------------------------
   Plain CSS (component-scoped)
   ------------------------- */
const styles = `
/* container positions near bottom-right; message box sits to left of whatsapp button */
.wfb-whatsapp {
  position: fixed;
  right: 20px;
  bottom: 20px;
  background-color: #25D366;
  border-radius: 50%;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(0,0,0,0.25);
  cursor: pointer;
  z-index: 1001;
  transition: transform 180ms ease, box-shadow 180ms ease;
  text-decoration: none;
}
.wfb-whatsapp:hover { transform: scale(1.08); box-shadow: 0 10px 30px rgba(0,0,0,0.32); }

/* message box */
.wfb-message {
  position: fixed;
  right: 92px; /* space left of whatsapp button (adjust if needed) */
  bottom: 22px;
  z-index: 1002;
  max-width: 320px;
  width: calc(100% - 140px);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 18px 40px rgba(14,43,81,0.12);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  transform-origin: bottom right;
  transition: transform 220ms cubic-bezier(.2,.9,.3,1), opacity 180ms ease;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  color: #072033;
}

/* show/hide classes */
.wfb-show { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
.wfb-hide { opacity: 0; transform: translateY(8px) scale(0.98); pointer-events: none; }

/* left inside message */
.wfb-message__left { display:flex; gap:10px; align-items:center; flex:1 1 auto; min-width:0; }
.wfb-phone-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
}
.wfb-gif {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
.wfb-message__text { display:flex; flex-direction:column; min-width:0; }
.wfb-label { font-size:12px; color:#6b7280; margin-bottom:2px; }
.wfb-number { font-weight:700; color:#072033; text-decoration:none; }

/* close button */
.wfb-close {
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  width:34px;
  height:34px;
  border-radius:8px;
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  color:#6b7280;
}
.wfb-close:hover { background: rgba(14,43,81,0.04); color:#0e2b51; }

/* responsive behaviour */
@media (max-width:520px) {
  .wfb-message { right: 80px; bottom: 16px; max-width: 260px; padding:8px 10px; }
  .wfb-phone-icon { width:36px; height:36px; }
  .wfb-gif { width:34px; height:34px; }
  .wfb-number { font-size:14px; }
}


@media (max-width:380px) { .wfb-message { display:none; } }
`;
